import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Activity, Search, User, Clock, Plus, Edit, Trash2, Eye,
  LogIn, LogOut, Shield, CheckCircle2, XCircle, ArrowRight,
  FileText, Users, FolderKanban, Receipt, Globe, Server,
  TicketCheck, Briefcase, Calendar, Settings, CreditCard, KeyRound
} from 'lucide-react';

const moduleOptions = [
  { value: 'auth', label: 'Authentication' },
  { value: 'clients', label: 'Clients' },
  { value: 'leads', label: 'Leads' },
  { value: 'projects', label: 'Projects' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'tickets', label: 'Tickets' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'payments', label: 'Payments' },
  { value: 'expenses', label: 'Expenses' },
  { value: 'amc', label: 'AMC/Retainers' },
  { value: 'employees', label: 'Employees' },
  { value: 'leaves', label: 'Leaves' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'payroll', label: 'Payroll' },
  { value: 'domains', label: 'Domains' },
  { value: 'hosting', label: 'Hosting' },
  { value: 'ssl', label: 'SSL' },
  { value: 'credentials', label: 'Credentials' },
  { value: 'dlt', label: 'DLT' },
  { value: 'users', label: 'Users' },
  { value: 'roles', label: 'Roles' },
  { value: 'settings', label: 'Settings' },
];

const actionOptions = [
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'view', label: 'View' },
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'approve', label: 'Approve' },
  { value: 'reject', label: 'Reject' },
];

const getActionIcon = (action) => {
  const map = {
    create: <Plus className="h-3.5 w-3.5" />,
    update: <Edit className="h-3.5 w-3.5" />,
    delete: <Trash2 className="h-3.5 w-3.5" />,
    view: <Eye className="h-3.5 w-3.5" />,
    login: <LogIn className="h-3.5 w-3.5" />,
    logout: <LogOut className="h-3.5 w-3.5" />,
    approve: <CheckCircle2 className="h-3.5 w-3.5" />,
    reject: <XCircle className="h-3.5 w-3.5" />,
  };
  return map[action] || <Activity className="h-3.5 w-3.5" />;
};

const getActionColor = (action) => {
  const map = {
    create: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    update: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    delete: 'bg-red-500/10 text-red-500 border-red-500/20',
    view: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    login: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
    logout: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    approve: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    reject: 'bg-red-500/10 text-red-500 border-red-500/20',
  };
  return map[action] || 'bg-gray-500/10 text-gray-400 border-gray-500/20';
};

const getModuleIcon = (module) => {
  const map = {
    auth: <Shield className="h-4 w-4" />,
    clients: <Users className="h-4 w-4" />,
    leads: <Briefcase className="h-4 w-4" />,
    projects: <FolderKanban className="h-4 w-4" />,
    tasks: <CheckCircle2 className="h-4 w-4" />,
    tickets: <TicketCheck className="h-4 w-4" />,
    invoices: <Receipt className="h-4 w-4" />,
    payments: <CreditCard className="h-4 w-4" />,
    expenses: <FileText className="h-4 w-4" />,
    amc: <FileText className="h-4 w-4" />,
    employees: <Users className="h-4 w-4" />,
    leaves: <Calendar className="h-4 w-4" />,
    attendance: <Clock className="h-4 w-4" />,
    payroll: <CreditCard className="h-4 w-4" />,
    domains: <Globe className="h-4 w-4" />,
    hosting: <Server className="h-4 w-4" />,
    ssl: <Shield className="h-4 w-4" />,
    credentials: <KeyRound className="h-4 w-4" />,
    dlt: <FileText className="h-4 w-4" />,
    users: <Users className="h-4 w-4" />,
    roles: <Shield className="h-4 w-4" />,
    settings: <Settings className="h-4 w-4" />,
  };
  return map[module] || <Activity className="h-4 w-4" />;
};

const getModuleColor = (module) => {
  const map = {
    auth: 'text-cyan-500', clients: 'text-blue-500', leads: 'text-amber-500',
    projects: 'text-primary', tasks: 'text-accent', tickets: 'text-purple-500',
    invoices: 'text-emerald-500', payments: 'text-emerald-500', expenses: 'text-red-400',
    amc: 'text-orange-500', employees: 'text-blue-500', leaves: 'text-amber-500',
    attendance: 'text-cyan-500', payroll: 'text-emerald-500', domains: 'text-blue-500',
    hosting: 'text-purple-500', ssl: 'text-green-500', credentials: 'text-amber-500',
    dlt: 'text-pink-500', users: 'text-blue-500', roles: 'text-purple-500',
    settings: 'text-gray-400',
  };
  return map[module] || 'text-primary';
};

const formatTime = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffDay > 1) return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ' at ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  if (diffDay === 1) return 'Yesterday at ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  if (diffHr > 0) return `${diffHr}h ago`;
  if (diffMin > 0) return `${diffMin}m ago`;
  return 'Just now';
};

const groupByDate = (logs) => {
  const groups = {};
  logs.forEach(log => {
    const dateKey = new Date(log.timestamp).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(log);
  });
  return groups;
};

export const ActivityLogPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [activityData, setActivityData] = useState([]);
  const [userMap, setUserMap] = useState({});

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [logsRes, usersRes] = await Promise.all([
          api.get('/activity-logs'),
          api.get('/users'),
        ]);
        if (logsRes.data.success) setActivityData(logsRes.data.data);
        if (usersRes.data.success) {
          const map = {};
          usersRes.data.data.forEach(u => {
            map[u.id] = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
          });
          setUserMap(map);
        }
      } catch {}
    };
    fetchAll();
  }, []);

  // Map API data to the format the page expects
  const mappedLogs = useMemo(() => activityData.map(log => ({
    id: log.id,
    action: log.action || 'create',
    module: log.model_type || 'system',
    target: log.target_name || '',
    detail: log.description || `${log.action} on ${log.model_type}`,
    user: userMap[log.user_id] || log.user_id || 'System',
    user_id: log.user_id,
    timestamp: log.created_at || new Date().toISOString(),
    ip: log.ip_address || null,
  })), [activityData, userMap]);

  const uniqueUsers = useMemo(() => {
    const seen = new Map();
    mappedLogs.forEach(l => { if (l.user_id && !seen.has(l.user_id)) seen.set(l.user_id, l.user); });
    return [...seen.entries()]; // [[user_id, name], ...]
  }, [mappedLogs]);

  const filtered = useMemo(() => {
    return mappedLogs.filter(log => {
      if (moduleFilter !== 'all' && log.module !== moduleFilter) return false;
      if (actionFilter !== 'all' && log.action !== actionFilter) return false;
      if (userFilter !== 'all' && log.user_id !== userFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!log.detail.toLowerCase().includes(q) && !log.target.toLowerCase().includes(q) && !log.user.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [mappedLogs, searchQuery, moduleFilter, actionFilter, userFilter]);

  const grouped = useMemo(() => groupByDate(filtered), [filtered]);

  const todayCount = useMemo(() => {
    const todayStr = new Date().toDateString();
    return mappedLogs.filter(l => new Date(l.timestamp).toDateString() === todayStr).length;
  }, [mappedLogs]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="activity-log-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Activity Log</h1>
          <p className="text-muted-foreground">Audit trail of all system actions</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card" data-testid="stat-total-actions">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Activity className="h-5 w-5 text-primary" /></div>
              <div><p className="text-2xl font-bold">{mappedLogs.length}</p><p className="text-xs text-muted-foreground">Total Actions</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="stat-today-actions">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><Clock className="h-5 w-5 text-emerald-500" /></div>
              <div><p className="text-2xl font-bold">{todayCount}</p><p className="text-xs text-muted-foreground">Today</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="stat-active-users">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><User className="h-5 w-5 text-blue-500" /></div>
              <div><p className="text-2xl font-bold">{uniqueUsers.length}</p><p className="text-xs text-muted-foreground">Active Users</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="stat-modules-touched">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center"><Settings className="h-5 w-5 text-purple-500" /></div>
              <div><p className="text-2xl font-bold">{new Set(mappedLogs.map(l => l.module)).size}</p><p className="text-xs text-muted-foreground">Modules</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search activity..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="activity-search" />
            </div>
            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="activity-module-filter"><SelectValue placeholder="Module" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Modules</SelectItem>{moduleOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-full sm:w-[140px]" data-testid="activity-action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Actions</SelectItem>{actionOptions.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="activity-user-filter"><SelectValue placeholder="User" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Users</SelectItem>{uniqueUsers.map(([uid, name]) => <SelectItem key={uid} value={uid}>{name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      {filtered.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Activity className="h-12 w-12 mb-4 opacity-50" />
          <p>No activity found matching your filters</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-8" data-testid="activity-timeline">
          {Object.entries(grouped).map(([date, logs]) => (
            <div key={date}>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium text-muted-foreground px-2 whitespace-nowrap">{date}</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <div className="space-y-1">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="group flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors"
                    data-testid={`activity-row-${log.id}`}
                  >
                    {/* Action indicator */}
                    <div className={`mt-0.5 h-8 w-8 shrink-0 rounded-full border flex items-center justify-center ${getActionColor(log.action)}`}>
                      {getActionIcon(log.action)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{log.user}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{log.action}</Badge>
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <span className={getModuleColor(log.module)}>{getModuleIcon(log.module)}</span>
                          <span className="capitalize">{log.module}</span>
                        </span>
                        {log.target && log.target !== 'System' && (
                          <>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm font-medium">{log.target}</span>
                          </>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5 truncate">{log.detail}</p>
                    </div>

                    {/* Timestamp */}
                    <div className="shrink-0 text-right">
                      <p className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                      </p>
                      {log.ip && <p className="text-[10px] text-muted-foreground/60 mt-0.5">{log.ip}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {filtered.length > 0 && (
        <p className="text-center text-xs text-muted-foreground py-4">Showing {filtered.length} of {mappedLogs.length} entries</p>
      )}
    </div>
  );
};
